import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';

import { AuthContext, type AuthContextValue, type AuthStatus } from '@/features/auth/authContext';
import { mapAuthError } from '@/features/auth/mapAuthError';
import { useAnalyticsTracker } from '@/features/auth/useAnalyticsTracker';
import { usePresenceHeartbeat } from '@/features/auth/usePresenceHeartbeat';
import { apiClient } from '@/lib/api/client';
import { env } from '@/lib/env';
import { getSupabaseClient } from '@/lib/supabase';

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Owns the Supabase session and installs the access token into the API client.
 *
 * Two responsibilities, deliberately no more:
 * 1. Subscribe to `onAuthStateChange` and expose the session to React.
 * 2. Hand the current access token to `apiClient` so every authenticated call
 *    carries a bearer without any feature importing Supabase.
 *
 * Sign-in UI, redirects and onboarding live elsewhere. Keeping this provider
 * thin is what makes it testable and keeps auth state out of every page.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(env.hasSupabase ? 'loading' : 'anonymous');
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const applySession = useCallback((next: Session | null) => {
    setSession(next);
    setUser(next?.user ?? null);
    setStatus(next ? 'authenticated' : 'anonymous');
  }, []);

  useEffect(() => {
    if (!env.hasSupabase) {
      // No credentials configured: treat every visitor as anonymous so the
      // public pages still render, and leave authenticated routes to redirect.
      apiClient.setTokenProvider(() => null);
      return undefined;
    }

    const supabase = getSupabaseClient();

    apiClient.setTokenProvider(async () => {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token ?? null;
    });

    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) applySession(data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      applySession(next);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [applySession]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(mapAuthError(error));
  }, []);

  const signUpWithPassword = useCallback(
    async (email: string, password: string, fullName: string) => {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw new Error(mapAuthError(error));
    },
    [],
  );

  const signInWithOAuth = useCallback(async (provider: 'google' | 'github') => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw new Error(mapAuthError(error));
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    if (error) throw new Error(mapAuthError(error));
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw new Error(mapAuthError(error));
  }, []);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(mapAuthError(error));
  }, []);

  usePresenceHeartbeat(status === 'authenticated');
  useAnalyticsTracker(status === 'authenticated');

  const getAccessToken = useCallback(async () => {
    if (!env.hasSupabase) return null;
    const { data } = await getSupabaseClient().auth.getSession();
    return data.session?.access_token ?? null;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      user,
      isReady: status !== 'loading',
      isAuthenticated: status === 'authenticated',
      signInWithPassword,
      signUpWithPassword,
      signInWithOAuth,
      sendPasswordReset,
      updatePassword,
      signOut,
      getAccessToken,
    }),
    [
      status,
      session,
      user,
      signInWithPassword,
      signUpWithPassword,
      signInWithOAuth,
      sendPasswordReset,
      updatePassword,
      signOut,
      getAccessToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
